'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Modal } from '@shared/ui/modal/Modal';
import { PrimaryButton } from '@shared/ui/button/PrimaryButton';
import { AuthButton } from '@shared/ui/button/AuthButton';
import { Input } from '@shared/ui/inputs/Input';
import { PasswordInput } from '@shared/ui/inputs/PasswordInput';
import { Typography } from '@shared/ui/typography/Typography';
import { Icon } from '@shared/ui/icons/Icon';
import type { BaseModalProps } from '@shared/types/modal.types';
import { loginUser, loginWithGoogle } from '@entities/user/api';
import { useModals } from '@shared/hooks/useModal';
import { useAuthStore } from '@shared/lib/store/auth-store';
import { rateLimiter, RATE_LIMITS } from '@shared/lib/security/rate-limiter';
import { sanitizeEmail } from '@shared/lib/security/sanitize';
import type { LoginRequest } from '@hummii/shared-types';
import type { AuthModalIntent } from '@shared/types/modal.types';
import { useModal } from '@shared/hooks/useModal';
import { useRouter } from 'next/navigation';

interface LoginModalProps extends BaseModalProps {
  onSuccess?: () => void;
}

/**
 * Login Modal - user login form
 * Usage: const { open } = useModal('login'); open({ intent: { intendedRole: UserRole.CLIENT } });
 */
export function LoginModal({
  isOpen,
  onClose,
  onSuccess,
}: LoginModalProps) {
  // Get intent from modal props
  const { props: modalProps } = useModal('login');
  const intent = modalProps?.intent as AuthModalIntent | undefined;
  const router = useRouter();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = useTranslations('auth.login');
  const { openModal } = useModals();
  const { login } = useAuthStore();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = t('errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.emailInvalid');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('errors.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    // Check rate limit
    const rateCheck = rateLimiter.checkLimit('login', RATE_LIMITS.LOGIN);
    if (!rateCheck.isAllowed) {
      setErrors({
        submit: t('errors.tooManyAttempts', {
          minutes: Math.ceil((rateCheck.retryAfter || 0) / 60)
        })
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      // Sanitize email
      const sanitizedEmail = sanitizeEmail(formData.email);
      if (!sanitizedEmail) {
        setErrors({ email: t('errors.emailInvalid') });
        setIsSubmitting(false);
        return;
      }

      // Prepare login data
      const loginData: LoginRequest = {
        email: sanitizedEmail,
        password: formData.password,
      };

      // Call API
      const response = await loginUser(loginData);

      // Security: Don't log user data in production
      if (process.env.NODE_ENV === 'development') {
        console.log('[LoginModal] Login successful');
      }

      // Save user to auth store with actors from response
      // Security: Tokens are in HTTP-only cookies, NOT in store (prevents XSS)
      login(response.user, response.activeActor, response.availableActors);

      // Close modal
      onClose();

      // Execute intent actions
      if (intent?.onSuccessAction) {
        intent.onSuccessAction();
      } else if (intent?.redirectAfterAuth) {
        router.push(intent.redirectAfterAuth);
      }

      // Call success callback
      onSuccess?.();

      // Reset form
      setFormData({
        email: '',
        password: '',
      });
    } catch (error) {
      console.error('Login failed:', error);

      // Handle specific error cases
      if (error instanceof Error) {
        // Check for specific error types
        if (error.message.includes('401')) {
          setErrors({ submit: t('errors.invalidCredentials') });
        } else if (error.message.includes('403')) {
          setErrors({ submit: t('errors.emailNotVerified') });
        } else if (error.message.includes('404')) {
          setErrors({ submit: t('errors.userNotFound') });
        } else {
          setErrors({ submit: t('errors.loginFailed') });
        }
      } else {
        setErrors({ submit: t('errors.loginFailed') });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      animation="fade"
      showCloseButton={false}
      className="!max-w-[1070px] !w-[1070px] !h-[550px] !rounded-[30px] overflow-hidden"
    >
      <div
        className="flex flex-col md:flex-row h-full"
      >
        {/* Left Side - Decorative Image */}
        <div className="hidden md:flex md:w-1/2 items-center justify-center pl-[40px] py-8">
          <div className="relative w-full max-w-[420px] h-[420px]">
            <Image
              src="/images/modal/login.svg"
              alt="Login illustration"
              fill
              className="object-contain"
              priority
            />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full md:w-1/2 p-6 md:py-8 md:pr-[40px]">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Title */}
            <div className="mb-6">
              <Typography variant="h2" className="mb-2">
                {t('title')}
              </Typography>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('fields.email')}
                error={!!errors.email}
                errorText={errors.email}
                leftIcon="email"
                leftIconColor="success"
              />
            </div>

            {/* Password */}
            <div className="space-y-1">
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('fields.password')}
                error={!!errors.password}
                errorText={errors.password}
              />
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link href="/forgot-password" className="inline">
                <Typography
                  variant="bodySm"
                  color="accent"
                  weight="semibold"
                  className="inline hover:underline"
                >
                  {t('links.forgotPassword')}
                </Typography>
              </Link>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-feedback-error/10 border border-feedback-error rounded-lg">
                <Typography variant="bodySm" color="error">
                  {errors.submit}
                </Typography>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-4 pt-4">
              <PrimaryButton
                type="submit"
                fullWidth
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('buttons.loggingIn') : t('buttons.login')}
              </PrimaryButton>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-[1px] bg-border-secondary" />
                <Typography variant="bodySm" color="tertiary">
                  {t('buttons.or')}
                </Typography>
                <div className="flex-1 h-[1px] bg-border-secondary" />
              </div>

              {/* Social Login Buttons */}
              <div className="flex flex-row gap-3">
                <AuthButton
                  variant="secondary"
                  fullWidth
                  onClick={() => loginWithGoogle()}
                >
                  <Icon name="google" size="md" color="inherit" />
                  {t('buttons.google')}
                </AuthButton>
              </div>

              {/* Register Link */}
              <div className="text-center pt-2">
                <Typography variant="bodySm" color="secondary" className="inline">
                  {t('footer.noAccount')}{' '}
                </Typography>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    openModal('registration');
                  }}
                  className="inline bg-none border-none p-0 cursor-pointer hover:underline"
                >
                  <Typography 
                    variant="bodySm" 
                    color="accent" 
                    weight="semibold"
                    className="inline"
                  >
                    {t('footer.register')}
                  </Typography>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
